import { useEffect, useState } from 'react';
import type { HTMLAttributes } from 'react';
import { Modal, Table, Card, Tag } from 'antd';

import {
	useGetGroupedMaterialReservationsQuery, useLazyGetMaterialReservationsQuery,
	useUpdateMultipleMaterialReservationCutStatusesMutation,
} from '../../redux/api/material';
import {
	Material,
	MaterialReservation,
} from '../../interfaces/MaterialInventory.interface';
import { Lineitem } from '../../interfaces/Ordel.Interface';
import { Link } from 'react-router-dom';
import MaterialReservationsTable from '../MaterialReservationsTable';

interface GroupedMaterialReservation {
	key: string;
	material: Material;
	order_line_item: Lineitem;
	material_cut_length: number;
	total_reserved_length: number;
	total_material_cut_quantity: number;
	reservations: MaterialReservation[];
	reservations_count: number;
	material_type: string;
	material_shape: string;
	display_size: string;
	floor_generated?: boolean;
}

const MaterialReservationsGroupedTable = () => {
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [{ confirm }, contextHolder] = Modal.useModal();
	const { data, isLoading, isFetching, refetch } = useGetGroupedMaterialReservationsQuery(
		{
			page: currentPage,
		},
		{
			refetchOnFocus: true,
			refetchOnMountOrArgChange: true,
		}
	);

	const [loading, setLoading] = useState<boolean>(false);

	const [data_reservations_source, setDataReservationsSource] = useState<MaterialReservation[]>([]);

	const [triggerGetMaterialReservations] = useLazyGetMaterialReservationsQuery();

	const [updateMultipleReservationStatuses] = useUpdateMultipleMaterialReservationCutStatusesMutation();

	function handleUpdateMultipleReservationStatuses(
		reservations: MaterialReservation[],
		status: string
	) {
		updateMultipleReservationStatuses({
			ids: reservations.map((x) => x.id),
			material_cut_status: status,
		})
			.unwrap()
			.then(() => refetch());
	}

	const tableColumns: any = [
		{
			title: 'Material',
			width: '25%',
			render: (record: GroupedMaterialReservation) => (
				<Link to={`/material/materials/?search=${record.material_type}`}>
					{record.material_type} - {record.material_shape}
				</Link>
			),
		},
		{
			title: 'Size',
			width: '10%',
			render: (record: GroupedMaterialReservation) =>
				record?.display_size || '',
		},
		{
			title: 'Cut Length',
			width: '10%',
			render: (record: GroupedMaterialReservation) =>
				record.material_cut_length,
		},
		{
			title: 'Cut Quantity',
			width: '10%',
			render: (record: GroupedMaterialReservation) =>
				record.total_material_cut_quantity,
		},
		{
			title: 'Total Length',
			width: '10%',
			render: (record: GroupedMaterialReservation) =>
				record.total_reserved_length,
		},
		{
			title: 'Total Bars',
			width: '10%',
			render: (record: GroupedMaterialReservation) => record.reservations_count,
		},
		{
			title: 'Order',
			width: '10%',
			render: (record: GroupedMaterialReservation) => (
				<Link to={`/orders/${record.order_line_item?.order?.id}`}>
					{record.order_line_item?.order?.name}
				</Link>
			),
			sorter: true,
		},
		{
			title: 'Line Item',
			width: '20%',
			render: (record: GroupedMaterialReservation) => (
				<>
					<Link to={`/orders/line-items/${record.order_line_item?.id}`}>
						{record.order_line_item?.name}
					</Link>
					{record.floor_generated ? (
						<div>
							<Tag color="gold" data-testid="fastlane-more-material">
								More material
							</Tag>
						</div>
					) : null}
				</>
			),
			sorter: true,
		},
	];

	const ExpandedRowContent = (reservations: MaterialReservation[], groupKey: string) => {
		return (
			<Card
				variant={'outlined'}
				style={{
				maxWidth: '98%',
				margin: 'auto',
			}}>
				<MaterialReservationsTable
					data={data_reservations_source.filter((x) => x.group_key === groupKey)}
					currentPage={1}
					recordsTotal={data_reservations_source.length}
					onChangePage={()=> {
						return true
					}}
					isLoading={false}
					refetchData={()=>{

						console.error('Not Implement')
					}}
				/>

				{/*<pre>*/}
				{/*	{JSON.stringify(reservations, null, 2)}*/}
				{/*</pre>*/}
			</Card>
		);
	}

	const handleExpand = async (expanded: boolean, record: GroupedMaterialReservation) => {
		if (expanded) {
			try {

				setLoading(true);

				const groupKey = record.key;

				const response = await triggerGetMaterialReservations({ group_key: groupKey }).unwrap();

				const updatedResults = response.results.map((reservation: MaterialReservation) => ({
					...reservation,
					group_key: groupKey,
				}));

				const existingGroupKeys = new Set(data_reservations_source.map(item => item.group_key));

				const newReservations = updatedResults.filter(reservation => !existingGroupKeys.has(reservation.group_key));

				if (newReservations.length > 0) {

					setDataReservationsSource(prevData => [...prevData, ...newReservations]);

				}

				setLoading(false);

			} catch (error) {

				console.error(error);

			}
		}
	};

	const pageChanged = async (page: number) => {
		setCurrentPage(page);
	};

	useEffect(() => {
		setLoading(true);
		refetch(
			{
				page: currentPage,
			}
		);
		setLoading(false);
	}, [currentPage]);

	const groupedTableLoading = isLoading || isFetching || loading;
	const groupedRows = (data?.results || []) as GroupedMaterialReservation[];
	const groupedTableReady = Boolean(data) && !groupedTableLoading;

	return (
		<div
			data-testid="fastlane-material-reservations-grouped-table"
			data-ready={groupedTableReady ? 'true' : 'false'}
		>
			<Table
				scroll={{ x: '360px' }}
				columns={tableColumns}
				dataSource={groupedRows}
				loading={groupedTableLoading}
				onRow={(record) => ({
					'data-testid': 'fastlane-material-reservation-group-row',
					'data-material-reservation-group-key': record.key,
				} as HTMLAttributes<HTMLElement>)}
				expandable={{
					expandedRowRender: record => ExpandedRowContent(record.reservations_count, record.key),
					rowExpandable: record => Number(record.reservations_count) > 0,
					onExpand: handleExpand,
				}}
				rowKey="key"
				pagination={
					{
						pageSize: 20,
						current: currentPage,
						pageSizeOptions: [],
						total: data?.count,
						onChange: async (page) => {
							await pageChanged(page);
						},
						defaultPageSize: 20,
						showSizeChanger: false,
					}
				}
			/>

			{contextHolder}
		</div>
	);
};

export default MaterialReservationsGroupedTable;
